import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowDown } from 'lucide-react';
import { MaskLines, MaskReveal } from './MaskReveal';
import { HERO } from '../../lib/site-data';

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink-900"
    >
      {/* Ambient background elements */}
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div className="absolute inset-0 noise opacity-60" />
      {/* Signal blue accent light — behind the image panel */}
      <div className="pointer-events-none absolute right-[-8%] top-1/2 -translate-y-1/2 h-[720px] w-[720px] rounded-full bg-signal-500/15 blur-[160px]" />
      {/* Secondary warm accent from image glow */}
      <div className="pointer-events-none absolute right-[10%] bottom-[20%] h-[380px] w-[380px] rounded-full bg-amber-500/8 blur-[140px]" />

      {/* Frame corners */}
      <div className="pointer-events-none absolute top-24 left-4 md:left-12 h-6 w-6 border-t border-l border-white/30 z-10" />
      <div className="pointer-events-none absolute top-24 right-4 md:right-12 h-6 w-6 border-t border-r border-white/30 z-10" />
      <div className="pointer-events-none absolute bottom-10 left-4 md:left-12 h-6 w-6 border-b border-l border-white/30 z-10" />
      <div className="pointer-events-none absolute bottom-10 right-4 md:right-12 h-6 w-6 border-b border-r border-white/30 z-10" />

      {/* Content grid */}
      <div
        className="relative z-10 min-h-[100svh] container-editorial grid gap-y-10 lg:gap-x-14"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr)',
          paddingTop: 'clamp(96px, 14vh, 140px)',
          paddingBottom: 'clamp(72px, 10vh, 108px)',
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10 items-center h-full">
          {/* Left column — headline + copy + CTAs */}
          <div className="lg:col-span-6 flex flex-col justify-center">
            <MaskReveal immediate delay={0.3}>
              <span className="eyebrow">{HERO.eyebrow}</span>
            </MaskReveal>

            <h1
              data-testid="hero-headline"
              className="display-heading text-white leading-[0.98] mt-6 md:mt-8"
              style={{ fontSize: 'clamp(34px, 4.8vw, 68px)', letterSpacing: '-0.045em' }}
            >
              <MaskLines lines={HERO.headline} immediate baseDelay={0.5} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="mt-7 text-white/80 text-[15px] md:text-lg leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              {HERO.subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.8 }}
              className="mt-7 flex flex-wrap gap-3"
            >
              <a href={HERO.primaryCta.href} data-testid="hero-cta-primary" className="btn-pill btn-solid">
                {HERO.primaryCta.label}
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={HERO.secondaryCta.href} data-testid="hero-cta-secondary" className="btn-pill btn-outline">
                {HERO.secondaryCta.label}
                <ArrowDown className="h-4 w-4" />
              </a>
            </motion.div>
          </div>

          {/* Right column — framed image panel */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/3] w-full max-w-[880px] mx-auto lg:ml-auto lg:mr-0">
              {/* Corner accents */}
              <span className="pointer-events-none absolute -top-2 -left-2 h-5 w-5 border-t-2 border-l-2 border-signal-500 z-20" />
              <span className="pointer-events-none absolute -top-2 -right-2 h-5 w-5 border-t-2 border-r-2 border-signal-500 z-20" />
              <span className="pointer-events-none absolute -bottom-2 -left-2 h-5 w-5 border-b-2 border-l-2 border-signal-500 z-20" />
              <span className="pointer-events-none absolute -bottom-2 -right-2 h-5 w-5 border-b-2 border-r-2 border-signal-500 z-20" />

              {/* Image frame */}
              <div className="relative h-full w-full overflow-hidden border border-white/10 bg-ink-800 shadow-[0_40px_120px_-20px_rgba(77,139,255,0.25),0_20px_60px_-15px_rgba(0,0,0,0.6)]">
                <motion.img
                  src={HERO.heroImage}
                  alt={HERO.heroImageAlt}
                  loading="eager"
                  data-testid="hero-image"
                  style={{ y: imgY, scale: imgScale }}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              </div>

              {/* Caption strip */}
              <div className="absolute -bottom-8 left-0 right-0 flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-signal-500 animate-pulse" />
                  A melting pot of global talent
                </span>
                <span>Vol. 01</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
